import { tickToRate } from '@chainflip/utils/tickMath';
import { useMutation } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useTracking from '@/shared/hooks/useTracking';
import { chainflipAssetMap } from '@/shared/utils/env';
import { type RpcRangeOrder } from '@/shared/utils/orders';
import { liquidityToTokenAmounts } from '@/shared/utils/tickMath';
import TokenAmount from '@/shared/utils/TokenAmount';
import usePoolPrice from './usePoolPrice';
import { useExtrinsicSDK } from './useStateChainAccount';
import { LiquidityProvisionEvents } from '../types/track';

export const useCloseRangeOrder = (order: RpcRangeOrder) => {
  const { sendExtrinsic, toast } = useExtrinsicSDK('setRangeOrder', 'close-range-order');
  const { rangeOrderPrice = BigNumber(0) } = usePoolPrice(order.asset);

  const { selectedAccount: polkadotAccount } = usePolkadot();
  const track = useTracking();

  const getTrackingProps = () => {
    const lowPrice = tickToRate(order.range.start, order.asset);
    const highPrice = tickToRate(order.range.end, order.asset);

    const tokenAmounts = liquidityToTokenAmounts({
      liquidity: order.liquidity.toString(),
      baseAsset: order.asset,
      currentRate: rangeOrderPrice,
      lowerTick: order.range.start,
      upperTick: order.range.end,
    });

    const earnedFeesBaseAsset = TokenAmount.fromAsset(
      order.fees_earned.base.toString(),
      order.asset,
    );
    const earnedFeesQuoteAsset = TokenAmount.fromAsset(order.fees_earned.quote.toString(), 'Usdc');

    return {
      path: window.location.pathname,
      scAccount: polkadotAccount?.idSs58 ?? '-',
      position: 'open_orders',
      type: 'limit',
      lowPrice: lowPrice.toFixed(),
      highPrice: highPrice.toFixed(),
      amountBaseAsset: tokenAmounts.baseAsset.toFixed() ?? '-',
      amountQuoteAsset: tokenAmounts.quoteAsset.toFixed() ?? '-',
      collectableFeeBaseAsset: earnedFeesBaseAsset.toFixed(),
      collectableFeeQuoteAsset: earnedFeesQuoteAsset.toFixed(),
    };
  };

  const getSuccessToastMessage = () => {
    const lowPrice = tickToRate(order.range.start, order.asset).toFixed(2);
    const highPrice = tickToRate(order.range.end, order.asset).toFixed(2);
    const asset = chainflipAssetMap[order.asset].symbol;
    const quote = chainflipAssetMap.Usdc.symbol;

    return `Range order from ${lowPrice} ${quote} to ${highPrice} ${quote} for ${asset} closed`;
  };

  return useMutation({
    mutationFn: async () => {
      toast.loading('Submitting transaction to the State Chain...');
      track(LiquidityProvisionEvents.CloseRangeOrder, {
        props: getTrackingProps(),
      });

      return sendExtrinsic({
        baseAsset: order.asset,
        quoteAsset: 'Usdc',
        orderId: BigInt(order.id),
        lowerTick: order.range.start,
        upperTick: order.range.end,
        maxBaseAmount: TokenAmount.fromAsset(0, order.asset),
        maxQuoteAmount: TokenAmount.fromAsset(0, 'Usdc'),
      });
    },

    onSuccess: () => {
      track(LiquidityProvisionEvents.CloseRangeOrderSuccess, {
        props: getTrackingProps(),
      });

      toast.success(getSuccessToastMessage());
    },

    onError: (err: Error) => {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });
};
